import { productImageAlt } from "@/lib/imageAlt";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { ShopifyProduct, formatPrice } from "@/lib/shopify";
import { cleanProductTitle } from "@/lib/productTitle";
import { dedupeProducts, uniqueDisplayNames } from "@/lib/productTitle";
import { diversifyRail, fetchTypePool } from "@/lib/railDiversity";

import { AdaptiveProductImage } from "./AdaptiveProductImage";
import { ProductGridSkeleton } from "./ProductCardSkeleton";
import { genuineOriginalPrice } from "@/lib/pricing";
import { listingPrice } from "@/lib/variants";

// Only statement pieces that photograph well on a homepage. A single OR query
// returns one type almost exclusively (97/100 sectionals), so the pool is built
// per type and then diversified.
const HERO_TYPES = [
  "Sofas",
  "Sectionals",
  "Accent Chairs",
  "Coffee Tables",
  "Dining Tables",
  "Standard Beds",
  "Console Tables",
  "Sideboards",
  "Floor Lamps",
  "Ottomans",
];

// No "On Sale" tab: Shopify supplies no real compare-at price above price in
// this catalogue, so a sale rail would have nothing honest to show.
const tabs = [
  { label: "Best Sellers", sortKey: "BEST_SELLING", reverse: false },
  { label: "New Arrivals", sortKey: "CREATED_AT", reverse: true },
  { label: "Under $250", sortKey: "PRICE", reverse: false },
];


const PER_TAB = 12;

// Titles that signal utilitarian / low-visual-quality listings
const BAD_WORDS = [
  "replacement", "cover", "slipcover", "cushion only", "parts", "kit", "frame only",
  "inflatable", "folding", "camping", "gaming", "rv ", "office cubicle", "plastic",
  "kids", "toddler", "juvenile", "pet ", "dog ", "massage", "futon", "bean bag",
  "set of 2 chairs cover", "unfinished", "diy",
];

// Words that usually accompany well-styled, premium imagery
const GOOD_WORDS = [
  "boucle", "bouclé", "velvet", "linen", "walnut", "oak", "marble", "travertine",
  "mid-century", "modern", "sculptural", "curved", "channel", "tufted", "leather",
  "solid wood", "rattan", "cane", "brass", "upholstered",
];

const CARD_ASPECT = 4 / 5;

/**
 * Image-quality score (0–100). This is the dominant ranking signal now:
 * resolution (sharpness headroom), framing (ratio vs. our card),
 * consistency across the gallery, gallery depth and alt-text richness.
 */
const imageQualityScore = (p: ShopifyProduct) => {
  const imgs = p.node.images?.edges || [];
  if (imgs.length === 0) return 0;
  const first = imgs[0].node;
  const w = first.width || 0;
  const h = first.height || 0;
  let score = 0;

  // Resolution — high-res store photos are sharper and better lit in practice
  const px = w * h;
  if (px >= 2_000_000) score += 34;
  else if (px >= 1_000_000) score += 27;
  else if (px >= 600_000) score += 19;
  else if (px >= 250_000) score += 10;
  else if (px > 0) score += 3;
  else score += 8; // unknown dimensions: neutral, don't punish

  // Framing — how close the native ratio is to the card frame (least cropping)
  if (w && h) {
    const ratio = w / h / CARD_ASPECT;
    const off = Math.abs(Math.log(ratio));
    if (off < 0.12) score += 24;
    else if (off < 0.3) score += 18;
    else if (off < 0.55) score += 11;
    else if (off < 0.9) score += 4;
  }

  // Consistent ratios across the gallery = a real photo shoot, not scraped files
  const ratios = imgs
    .map((e) => (e.node.width && e.node.height ? e.node.width / e.node.height : null))
    .filter((r): r is number => r !== null);
  if (ratios.length > 1) {
    const spread = Math.max(...ratios) - Math.min(...ratios);
    if (spread < 0.05) score += 14;
    else if (spread < 0.2) score += 8;
    else if (spread < 0.5) score += 3;
  }

  // Gallery depth — lifestyle/detail shots
  score += Math.min(imgs.length, 5) * 4;

  // Descriptive alt text correlates with curated, merchandised listings
  const alt = (first.altText || "").trim();
  if (alt.length > 25) score += 6;
  else if (alt.length > 0) score += 3;

  return score;
};

const scoreProduct = (p: ShopifyProduct) => {
  const title = p.node.title.toLowerCase();
  const price = parseFloat(p.node.priceRange.minVariantPrice.amount || "0");
  // Image quality carries ~70% of the weight
  let score = imageQualityScore(p) * 1.6;
  if (price >= 700) score += 10;
  else if (price >= 400) score += 6;
  else if (price >= 200) score += 2;
  for (const w of GOOD_WORDS) if (title.includes(w)) score += 3;
  if (title.length <= 70) score += 4;
  return score;
};

const isPresentable = (p: ShopifyProduct) => {
  const title = p.node.title.toLowerCase();
  if (BAD_WORDS.some((w) => title.includes(w))) return false;
  const imgs = p.node.images?.edges || [];
  if (imgs.length < 2) return false; // needs a real gallery
  const first = imgs[0]?.node;
  if (first?.width && first?.height) {
    const px = first.width * first.height;
    if (px < 200_000) return false; // too low-res for a hero placement
    const ratio = first.width / first.height;
    if (ratio < 0.5 || ratio > 2.2) return false; // unusable framing
  }
  const price = parseFloat(p.node.priceRange.minVariantPrice.amount || "0");
  if (price < 149) return false; // avoid cheap filler pieces
  return true;
};

// Short editorial caption: style cue + the room it belongs to. A cue may only
// restate what the supplier title says: "Full-grain leather" on a "Genuine
// Leather" chair or "Stone top" on faux terrazzo invented a material.
const STYLE_CUES: Array<[RegExp, string]> = [
  [/faux leather|vegan leather|leatherette|polyurethane/i, "Faux leather"],
  [/top[- ]grain leather/i, "Top-grain leather"],
  [/genuine leather|\bleather\b/i, "Leather"],
  [/faux (marble|terrazzo|stone|travertine)/i, "Stone look"],
  [/boucl|sherpa|teddy/i, "Soft bouclé"],
  [/velvet/i, "Plush velvet"],
  [/\blinen\b(?!\s+(cabinet|closet|tower|storage))/i, "Linen"],
  [/solid wood|solid (walnut|oak|acacia|mango|teak|pine)/i, "Solid wood"],
  [/walnut|oak|acacia|mango|teak/i, "Warm wood tones"],
  [/\bmarble\b/i, "Marble"],
  [/\btravertine\b/i, "Travertine"],
  [/\bterrazzo\b/i, "Terrazzo"],
  [/rattan|cane|wicker/i, "Woven texture"],
  [/brass|chrome|steel|iron|\bmetal\b/i, "Metal accents"],
  [/mid-?century|retro/i, "Mid-century lines"],
  [/curved|arch|round|sculpt/i, "Sculptural curves"],
  [/tufted|channel/i, "Tufted detail"],
  [/black|charcoal|noir/i, "Quiet contrast"],
];

const ROOM_BY_TYPE: Record<string, string> = {
  Sofas: "Living room",
  Sectionals: "Living room",
  "Love Seats": "Living room",
  "Accent Chairs": "Reading corner",
  "Coffee Tables": "Living room",
  "Console Tables": "Entryway",
  Sideboards: "Dining room",
  "Dining Tables": "Dining room",
  "Standard Beds": "Bedroom",
  "Floor Lamps": "Living room",
  Ottomans: "Living room",
};

const getCaption = (p: ShopifyProduct) => {
  const title = p.node.title;
  const style = STYLE_CUES.find(([re]) => re.test(title))?.[1] || "Considered design";
  const room = ROOM_BY_TYPE[p.node.productType || ""] || "Everyday spaces";
  return `${style} · ${room}`;
};

// No character cut: the <h3> clamps at two lines, and slicing at 44 took the
// noun off the end of a long name ("… Gold Picture Fram…").
const shortTitle = (title: string, productType?: string) => cleanProductTitle(title, productType);

export const HomeProducts = () => {
  const [products, setProducts] = useState<ShopifyProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const trackRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const tab = tabs[activeTab];
        const data = await fetchTypePool(HERO_TYPES, 12, "available_for_sale:true", tab.sortKey, tab.reverse);
        if (cancelled) return;

        let pool = dedupeProducts(data.filter(isPresentable));

        if (activeTab === 2) {
          pool = pool.filter(
            (p) => parseFloat(p.node.priceRange?.minVariantPrice?.amount || "0") <= 250,
          );
        }

        // Rank by image quality, keep the best-looking slice, then diversify so a
        // single product type can't take over the rail.
        pool = [...pool].sort((a, b) => scoreProduct(b) - scoreProduct(a)).slice(0, 60);

        setProducts(
          diversifyRail(pool, { count: PER_TAB, seed: `home-${tab.label}` })
        );
      } catch {
        if (!cancelled) setProducts([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [activeTab]);


  const scrollBy = (dir: 1 | -1) => {
    const el = trackRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * el.clientWidth * 0.8, behavior: "smooth" });
  };

  const names = useMemo(() => uniqueDisplayNames(products), [products]);

  const cards = useMemo(
    () =>
      products.map((product) => {
        const { node } = product;
        const img = node.images?.edges?.[0]?.node;
        const second = node.images?.edges?.[1]?.node;
        const { price, compareAtPrice: compare, from: priceIsFrom } = listingPrice(node);
        const onSale = !!genuineOriginalPrice(parseFloat(price?.amount || "0"), compare?.amount, node.availableForSale !== false);
        return (
          <Link
            key={node.id}
            to={`/product/${node.handle}`}
            className="group snap-start shrink-0 w-[62%] sm:w-[38%] md:w-[27%] lg:w-[23%]"
          >
            <div className="aspect-[4/3] overflow-hidden relative bg-muted/20">
              {img && (
                <AdaptiveProductImage url={img.url} alt={productImageAlt(node.title, { productType: node.productType })} />
              )}
              {second && (
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                  <AdaptiveProductImage
                    url={second.url}
                    alt={productImageAlt(node.title, { productType: node.productType, index: 1, total: 2 })}
                    hoverZoom={false}
                  />
                </div>
              )}
            </div>
            <p className="mt-3 text-[9.5px] tracking-[0.18em] uppercase text-primary font-semibold">
              {getCaption(product)}
            </p>
            <h3 className="mt-1 text-[13px] font-medium text-foreground leading-tight line-clamp-2 group-hover:text-primary transition-colors">
              {shortTitle(names[node.handle] || node.title, node.productType)}
            </h3>
            {price && (
              <div className="mt-1 flex items-center gap-2">
                {onSale && (
                  <span className="text-[12px] text-muted-foreground line-through">
                    {formatPrice(compare!.amount, compare!.currencyCode)}
                  </span>
                )}
                <span
                  className={`text-[13px] font-semibold ${
                    onSale ? "text-destructive" : "text-foreground"
                  }`}
                >
                  {priceIsFrom && "From "}
                  {formatPrice(price.amount, price.currencyCode)}
                </span>
              </div>
            )}
          </Link>
        );
      }),
    [products, names]
  );

  return (
    <section id="products" className="w-[89%] max-w-[1140px] mx-auto pt-5 md:pt-4 pb-4 md:pb-6">
      <div className="flex items-center justify-between gap-3 mb-3 md:mb-4">
        <h2 className="text-[12px] md:text-[13px] tracking-[0.14em] md:tracking-[0.22em] uppercase font-bold text-foreground whitespace-nowrap">
          Shop the Collection
        </h2>
        <Link
          to="/categories"
          className="text-[10px] tracking-[0.12em] md:tracking-[0.18em] uppercase font-bold text-foreground hover:text-primary inline-flex items-center gap-1.5 whitespace-nowrap shrink-0"
        >
          <span className="hidden sm:inline">Shop all products</span>
          <span className="sm:hidden">View all</span> <ArrowRight className="h-3 w-3" />
        </Link>
      </div>

      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-1.5 overflow-x-auto scrollbar-hide -mx-[5.5%] px-[5.5%] md:mx-0 md:px-0">
          {tabs.map((tab, i) => (
            <button
              key={tab.label}
              onClick={() => setActiveTab(i)}
              className={`px-4 py-2 text-[11px] tracking-[0.12em] uppercase font-bold rounded-full shrink-0 transition-colors ${
                activeTab === i
                  ? "bg-foreground text-background"
                  : "text-muted-foreground hover:text-foreground hover:bg-secondary"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <div className="hidden md:flex items-center gap-2 shrink-0">
          <button
            onClick={() => scrollBy(-1)}
            aria-label="Previous products"
            className="h-9 w-9 rounded-full border border-border flex items-center justify-center text-foreground hover:bg-foreground hover:text-background transition-colors"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            onClick={() => scrollBy(1)}
            aria-label="Next products"
            className="h-9 w-9 rounded-full border border-border flex items-center justify-center text-foreground hover:bg-foreground hover:text-background transition-colors"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      {loading ? (
        <ProductGridSkeleton count={4} />
      ) : products.length === 0 ? (
        <p className="text-[13px] text-muted-foreground py-8 text-center">No products found.</p>
      ) : (
        <div
          key={activeTab}
          ref={trackRef}
          className="stagger-grid flex gap-3 md:gap-5 overflow-x-auto scrollbar-hide snap-x snap-mandatory -mx-[5.5%] px-[5.5%] md:mx-0 md:px-0 pb-2"
        >
          {cards}
        </div>
      )}
    </section>
  );
};
